/**
 * Overview: the three figures a VP of Student Success checks first after the
 * hero's, and the curve underneath them.
 *
 * The order is not analytical elegance, it is trust. The first question is
 * always "are my students actually in here", checked against the invoice, and
 * if that number is wrong or missing nothing below it gets read. So the
 * claimed count leads, and it is stated as a floor rather than a register,
 * because a student who has not named the institution is invisible to it.
 */

import {
  Card,
  CardBody,
  CardFootnote,
  CardHeader,
  CardTitle,
  ProvenanceNote,
  SectionHead,
  StateCard,
} from "~/app/components/primitives";
import { SECTION_LEDE, SECTION_TITLE } from "~/app/lib/admin-nav";
import {
  formatCount,
  formatDate,
} from "~/app/lib/format";
import type { CohortView } from "~/app/lib/demo-data";
import { annotationPositions } from "~/app/lib/demo-data";
import { DefinitionRows, KpiTile, TrendArea } from "~/app/panels/panel-charts";
import { CellValue } from "~/app/components/primitives";
import { bandById } from "~/app/lib/pricing";

/** The tile glyphs, 24-box, 1.8 stroke, currentColor. */
function TileIcon({ d }: { d: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      dangerouslySetInnerHTML={{ __html: d }}
    />
  );
}
const HELD_D = '<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/>';
const WAITING_D = '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.2 2"/>';
const MILESTONE_D = '<path d="M6 21V3.6"/><path d="M6 4.4h11.6l-2.6 4.1 2.6 4.1H6"/>';

export function OverviewPanel({ view }: { view: CohortView }) {
  const { institution, seat } = view;

  return (
    <section className="e-sec e-print-page">
      <SectionHead
        id="overview"
        title={SECTION_TITLE.overview}
        lede={SECTION_LEDE.overview}
      />

      {/* Three figures the hero does not carry. The hero holds claimed, with a
          mentor, milestones and applications; repeating any of them one
          scroll down is the Ledger v4 failure (a KPI row that restated the
          hero) and it was deleted there for that reason. "Students who joined
          to date" was the fourth until Sep 19 2026 (Thomas): with no join
          window on a live snapshot it was the claimed count again, and on the
          sample every student joined inside the term, so it was there too.
          The curve below shows the roster growing, week by week. */}
      <div className="e-kpi-row is-three">
        <KpiTile
          label="Mentorships held"
          cell={view.mentorshipsHeld}
          format={formatCount}
          provenance={view.matchedProvenance}
          hue={2}
          icon={<TileIcon d={HELD_D} />}
          note="Higher than the number of matched students, because some students work with more than one mentor."
        />
        <KpiTile
          label="Awaiting mentorship"
          cell={view.waitingTotal}
          format={formatCount}
          provenance={view.waitingProvenance}
          hue={4}
          icon={<TileIcon d={WAITING_D} />}
          note="No confirmed mentorship yet. The Mentorship section splits them by whether they have asked."
        />
        {/* "Students with a milestone", not "Milestones completed" (Sep 18
            2026): the hero already carries that label over the count of
            milestones, and this figure is the count of students with at least
            one. One label over two numbers would be a false claim. */}
        <KpiTile
          label="Students with a milestone"
          cell={view.milestones.students}
          format={formatCount}
          provenance={view.milestones.provenance}
          hue={3}
          icon={<TileIcon d={MILESTONE_D} />}
          note={`At least one milestone marked complete ${view.milestones.span}. Reported by the student, not verified by P3.`}
        />
      </div>

      <div className="e-grid">
        <Card variant="chart" span={2} plot="half">
          <CardHeader>
            <CardTitle sub="Weeks end on Sunday. The current partial week is not drawn.">
              Students on P3 over time
            </CardTitle>
          </CardHeader>
          <CardBody>
            {view.growth.drawable.length >= 2 ? (
              <div className="e-plot">
                <TrendArea
                  points={view.growth.drawable}
                  annotations={annotationPositions(view.growth)}
                  label={`Students claimed to ${institution.legalName} by week, ${view.windowLabel}`}
                />
              </div>
            ) : (
              <StateCard variant="building" title="The curve starts at ten students">
                A running total below ten students is not published, so there is
                not yet a second week to draw a line between. The curve begins at
                the first week your roster clears the reporting threshold.
              </StateCard>
            )}
          </CardBody>
          <CardFootnote>
            <ProvenanceNote provenance={view.growth.provenance} />
            {view.growth.annotations.length > 0 ? (
              <p className="e-note">
                Dashed rules are markers your team added:{" "}
                {view.growth.annotations.map((a) => a.label).join(", ")}. P3 draws
                them and asserts nothing about them.
              </p>
            ) : null}
          </CardFootnote>
        </Card>

        <Card solo>
          <CardHeader>
            <CardTitle sub="Mentors and administrators are free.">Capacity and plan</CardTitle>
          </CardHeader>
          <CardBody>
            <DefinitionRows
              rows={[
                {
                  label: "Band",
                  value: <span className="e-ui">{bandById(institution.capacityBand).name}</span>,
                },
                {
                  label: "Claimed",
                  value: (
                    <CellValue
                      cell={seat.claimed}
                      format={formatCount}
                      label="Students claimed"
                    />
                  ),
                },
                {
                  label: "Headroom",
                  value: (
                    <CellValue
                      cell={seat.available}
                      format={formatCount}
                      label="Headroom in the band"
                    />
                  ),
                },
                {
                  label: "Plan",
                  value: (
                    <span className="e-ui">
                      {institution.planInterval}, renews{" "}
                      {formatDate(institution.renewsAt)}
                    </span>
                  ),
                },
              ]}
            />
            {seat.overCapacity ? (
              <p className="e-note" style={{ marginTop: "var(--sp-3)" }}>
                Past your band. Nothing is blocked, nothing is charged; we talk at
                renewal.
              </p>
            ) : null}
          </CardBody>
          <CardFootnote>
            <p className="e-note">Not a count P3 bills on. Hidden from Viewer accounts.</p>
          </CardFootnote>
        </Card>
      </div>

    </section>
  );
}

